// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: e00e6e4b-daf4-4254-85ab-6953530a1090

// ---------------------------------------------------------------------------
// AI enrichment — draft the boring 80% of an application inventory, then make a
// human sign off on every single value.
//
// The problem this solves: a client hands over 500 applications with little
// more than a name and a vendor. A consultant then spends days writing
// descriptions and guessing typical hosting / disposition before the first
// interview even happens.
//
// THE RULE THIS SCREEN IS BUILT AROUND: AI output can never silently become
// fact. Concretely, in this file:
//   * /ai/enrich is only ever READ from — nothing it returns is written anywhere
//     until the user presses Accept.
//   * every proposed value is its own checkbox, and a proposal that would
//     OVERWRITE an existing value starts UNCHECKED. Only genuinely empty fields
//     are pre-checked.
//   * a value the model returns that is not a legal option for the field
//     (picklists) cannot be accepted at all — the checkbox is disabled.
//   * the confirm step states the counts (values, applications, overwrites)
//     before anything is saved.
//   * every fact sheet we touch gets fields.aiAssisted = true plus the list of
//     keys that started life as an AI draft, so it is auditable later.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, Checkbox, Chip, TextField, InputAdornment, Alert, AlertTitle,
  LinearProgress, CircularProgress, Tooltip, Divider, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScienceIcon from '@mui/icons-material/Science';
import RefreshIcon from '@mui/icons-material/Refresh';
import { allFieldsFor, resolvePicklist, FIELD_TYPES, PICKLISTS } from './schema';
import { getConfig } from './config';
import { getIdToken } from './auth';
import store from './store';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const AMBER = '#B26A00';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

// Rendering guard only — selection and enrichment always cover the full match set.
const ROW_CAP = 250;
// The API is asked for a chunk of applications at a time so the progress bar
// means something on a 500-app inventory.
const REQUEST_CHUNK = 20;

const isBlank = (v) => v === undefined || v === null || v === '';

// Fields we are willing to ask a model to draft. Anything genuinely specific to
// THIS client's environment (server names, IPs, owners, contract values) is
// deliberately absent — a model cannot know those, so offering them would
// manufacture plausible nonsense.
const CORE_FIELD_KEYS = [
  'description',
  'appPurpose',
  'hostingType',
  'sixRClassification',
  'applicationType',
  'vendorName',
];

// Standard discovery questions — the ones a vendor's public documentation can
// usually answer, so a draft is a useful starting point for the interview.
//
// This list MUST be a subset of `ALLOWED_FIELDS` in `backend/lambda/ai.js`. It
// used to hold nine keys against a backend that accepted none of them: the
// request was filtered to empty, the prompt read "draft ONLY these fields: ",
// and the user landed on a generic "no suggestions" message with no way to tell
// that the whole block was inert. Adding a key here without adding it there
// rebuilds that trap.
const DISCOVERY_FIELD_KEYS = [
  'ssoAvailability',
  'ssoProviders',
];

// The seven that were offered and are NOT coming back. These are not an
// oversight in the allow-list — they are answers a model has no way to hold.
// RTO, RPO and the target DR strategy are decisions this client makes with
// Optimum; patient-safety impact and whether an instance holds PHI depend on
// how this client deployed the product, not on what the product is. Drafting
// them would produce confident nonsense in exactly the fields a migration plan
// is judged on. `cmSaaS`/`cmIaaS` are genuine product facts, but they are
// booleans and the enrichment pipeline carries string values with a picklist
// check end to end; wiring them needs coercion on both sides, which is a change
// worth making deliberately rather than folding into this one.
const NOT_DRAFTABLE = [
  { keys: ['rto', 'rpo', 'targetDrStrategy'], why: 'recovery objectives and DR strategy are decisions made with the client, not properties of the product' },
  { keys: ['phi', 'patientSafetyImpact'], why: 'both depend on how this client deployed and uses the application' },
  { keys: ['cmSaaS', 'cmIaaS'], why: 'product facts, but boolean fields the drafting pipeline does not carry yet' },
];

const DEFAULT_FIELD_KEYS = ['description', 'appPurpose', 'hostingType', 'sixRClassification'];

const CONFIDENCE_STYLES = {
  high: { label: 'high', color: '#2E7D32', bg: '#E8F5E9' },
  medium: { label: 'medium', color: AMBER, bg: '#FFF4E0' },
  low: { label: 'low', color: '#B0342C', bg: '#FDECEA' },
};

const normalizeConfidence = (c) => {
  const v = String(c || '').toLowerCase();
  if (v.startsWith('h')) return 'high';
  if (v.startsWith('m')) return 'medium';
  if (v.startsWith('l')) return 'low';
  const n = Number(c);
  if (!Number.isNaN(n) && n > 0) {
    const scaled = n > 1 ? n / 100 : n;
    if (scaled >= 0.75) return 'high';
    if (scaled >= 0.5) return 'medium';
    return 'low';
  }
  return 'low'; // unknown confidence is treated as the weakest, never the strongest
};

// Turn whatever the model returned into a value this field can legally hold.
// Anything that does not fit comes back { ok:false } and cannot be accepted.
export function coerceSuggestion(field, raw) {
  if (raw === undefined || raw === null || raw === '') return { ok: false, value: '', reason: 'empty' };

  if (field.type === FIELD_TYPES.BOOLEAN) {
    if (typeof raw === 'boolean') return { ok: true, value: raw };
    const s = String(raw).trim().toLowerCase();
    if (['yes', 'true', 'y', '1'].includes(s)) return { ok: true, value: true };
    if (['no', 'false', 'n', '0'].includes(s)) return { ok: true, value: false };
    return { ok: false, value: String(raw), reason: 'not yes/no' };
  }

  if (field.type === FIELD_TYPES.SELECT || field.type === FIELD_TYPES.MULTISELECT
    || field.type === FIELD_TYPES.RATING || field.type === FIELD_TYPES.ARROWS) {
    const opts = resolvePicklist(field.picklist);
    if (!opts.length) return { ok: true, value: String(raw) };
    const s = String(raw).trim().toLowerCase();
    const match = opts.find((o) => String(o).toLowerCase() === s);
    if (match) return { ok: true, value: match };
    return { ok: false, value: String(raw), reason: 'not an allowed option' };
  }

  if (field.type === FIELD_TYPES.NUMBER || field.type === FIELD_TYPES.CURRENCY
    || field.type === FIELD_TYPES.PERCENT) {
    const n = Number(raw);
    if (Number.isNaN(n)) return { ok: false, value: String(raw), reason: 'not a number' };
    return { ok: true, value: n };
  }

  return { ok: true, value: String(raw) };
}

const displayValue = (v) => {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

// ---------------------------------------------------------------------------
// Demo mode — deterministic local placeholders so the workflow can be walked
// end to end before /ai/enrich exists. NOTHING here is knowledge; it is filler,
// and it is labelled as filler everywhere it appears.
// ---------------------------------------------------------------------------
const hashOf = (s) => {
  let h = 0;
  const str = String(s || '');
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};
const pickFrom = (list, seed) => list[hashOf(seed) % list.length];

export function demoSuggestionsFor(app, fieldKeys) {
  const f = app.fields || {};
  const name = app.name || 'this application';
  const vendor = f.vendorName || String(name).split(/[\s-]+/)[0];
  const out = {};
  const put = (k, value) => {
    out[k] = {
      value,
      confidence: 'low',
      rationale: 'DEMO MODE — generated locally from the application name and vendor. '
        + 'No AI service was called and this is not knowledge about this product. '
        + 'It exists only so the review-and-accept workflow can be demonstrated.',
    };
  };
  fieldKeys.forEach((k) => {
    switch (k) {
      case 'description':
        put(k, `[DEMO PLACEHOLDER] ${name} — supplied by ${vendor}. Replace with a verified description confirmed by the application owner.`);
        break;
      case 'appPurpose':
        put(k, `[DEMO PLACEHOLDER] What ${name} is used for has not been established. Confirm with the owning team.`);
        break;
      case 'vendorName':
        put(k, `${vendor}`);
        break;
      case 'hostingType':
        put(k, pickFrom(PICKLISTS.hostingType, `${name}|hosting`));
        break;
      case 'sixRClassification':
        put(k, pickFrom(PICKLISTS.sixR, `${name}|6r`));
        break;
      case 'applicationType':
        put(k, pickFrom(PICKLISTS.applicationType, `${name}|type`));
        break;
      case 'ssoAvailability':
        put(k, pickFrom(['Available', 'Not Available'], `${name}|sso`));
        break;
      case 'ssoProviders':
        put(k, '[DEMO PLACEHOLDER] SAML 2.0 / OIDC — confirm with the vendor.');
        break;
      case 'cmSaaS':
      case 'cmIaaS':
      case 'phi':
        put(k, hashOf(`${name}|${k}`) % 2 === 0);
        break;
      case 'rto':
        put(k, pickFrom(PICKLISTS.rto, `${name}|rto`));
        break;
      case 'rpo':
        put(k, pickFrom(PICKLISTS.rpo, `${name}|rpo`));
        break;
      case 'patientSafetyImpact':
        put(k, pickFrom(PICKLISTS.patientSafety, `${name}|ps`));
        break;
      case 'targetDrStrategy':
        put(k, pickFrom(PICKLISTS.targetDrStrategy, `${name}|dr`));
        break;
      default:
        put(k, `[DEMO PLACEHOLDER] draft value for ${name}`);
        break;
    }
  });
  return out;
}

// ---------------------------------------------------------------------------

export default function AiEnrichment({ tenantId }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('');   // '' | 'description' | 'sixRClassification' | 'hostingType'
  const [selected, setSelected] = useState(() => new Set());
  const [fieldKeys, setFieldKeys] = useState(() => new Set(DEFAULT_FIELD_KEYS));

  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState(0);
  const [runErr, setRunErr] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [demoMode, setDemoMode] = useState(false);

  const [proposals, setProposals] = useState([]);       // flat rows, see buildProposals
  const [accepted, setAccepted] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyProgress, setApplyProgress] = useState(0);
  const [summary, setSummary] = useState(null);

  // ---- schema lookups ------------------------------------------------------
  const fieldByKey = useMemo(() => {
    const m = {};
    allFieldsFor('Application').forEach((f) => { if (!m[f.key]) m[f.key] = f; });
    return m;
  }, []);
  const labelFor = useCallback((k) => (fieldByKey[k] ? fieldByKey[k].label : k), [fieldByKey]);

  const coreFields = CORE_FIELD_KEYS.filter((k) => fieldByKey[k]);
  const discoveryFields = DISCOVERY_FIELD_KEYS.filter((k) => fieldByKey[k]);
  const notDraftable = useMemo(
    () => NOT_DRAFTABLE
      .map((g) => ({ ...g, labels: g.keys.filter((k) => fieldByKey[k]).map((k) => fieldByKey[k].label) }))
      .filter((g) => g.labels.length),
    [fieldByKey]
  );

  // ---- data ----------------------------------------------------------------
  const reload = useCallback(() => {
    setLoading(true);
    setLoadErr('');
    store.listFactSheets(tenantId, { type: 'Application' })
      .then((rows) => setApps(rows || []))
      .catch(() => setLoadErr('Could not load applications for this workspace.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    setSelected(new Set());
    setProposals([]);
    setAccepted(new Set());
    setSummary(null);
    reload();
  }, [reload]);

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getIdToken()}`,
        ...(options.headers || {}),
      },
    });
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
    if (!res.ok) {
      const err = new Error(data.error || data.message || `request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  }, []);

  // ---- selection -----------------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps.filter((fs) => {
      const f = fs.fields || {};
      if (q) {
        const hay = `${fs.name || ''} ${f.vendorName || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (quickFilter === 'description' && !isBlank(f.description)) return false;
      if (quickFilter === 'sixRClassification'
        && (!isBlank(f.sixRClassification) || !isBlank(f.cmDisposition))) return false;
      if (quickFilter === 'hostingType' && !isBlank(f.hostingType)) return false;
      return true;
    });
  }, [apps, search, quickFilter]);

  const visibleRows = filtered.slice(0, ROW_CAP);
  const allMatchingSelected = filtered.length > 0 && filtered.every((fs) => selected.has(fs.id));
  const someMatchingSelected = filtered.some((fs) => selected.has(fs.id));

  const toggleApp = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAllMatching = () => {
    const next = new Set(selected);
    if (allMatchingSelected) filtered.forEach((fs) => next.delete(fs.id));
    else filtered.forEach((fs) => next.add(fs.id));
    setSelected(next);
  };
  const toggleField = (k) => {
    const next = new Set(fieldKeys);
    if (next.has(k)) next.delete(k); else next.add(k);
    setFieldKeys(next);
  };

  const selectedApps = useMemo(
    () => apps.filter((fs) => selected.has(fs.id)),
    [apps, selected]
  );
  const askedFields = useMemo(
    () => [...coreFields, ...discoveryFields].filter((k) => fieldKeys.has(k)),
    [coreFields, discoveryFields, fieldKeys]
  );

  // ---- turning an API response into review rows ----------------------------
  // Pre-check rule lives here and nowhere else: an empty field may be pre-checked,
  // an existing value must be opted into by hand.
  const buildProposals = useCallback((results, sourceApps, isDemo) => {
    const byId = new Map(sourceApps.map((a) => [a.id, a]));
    const rows = [];
    const preChecked = new Set();
    (results || []).forEach((r) => {
      const app = byId.get(r && r.id);
      if (!app) return;
      const suggestions = (r && r.suggestions) || {};
      Object.keys(suggestions).forEach((key) => {
        const field = fieldByKey[key];
        if (!field) return;                       // never invent a field the schema does not have
        const s = suggestions[key] || {};
        const coerced = coerceSuggestion(field, s.value);
        const current = (app.fields || {})[key];
        const currentEmpty = isBlank(current) || (field.type === FIELD_TYPES.BOOLEAN && current === undefined);
        const rowKey = `${app.id}::${key}`;
        if (coerced.ok && String(displayValue(coerced.value)) === String(displayValue(current))) return; // no-op
        rows.push({
          rowKey,
          appId: app.id,
          appName: app.name,
          fieldKey: key,
          fieldLabel: labelFor(key),
          current,
          currentEmpty,
          value: coerced.value,
          usable: coerced.ok,
          reason: coerced.reason,
          confidence: normalizeConfidence(s.confidence),
          rationale: s.rationale || (isDemo ? 'Demo placeholder — no rationale.' : 'No rationale supplied by the model.'),
          demo: !!isDemo,
        });
        if (coerced.ok && currentEmpty) preChecked.add(rowKey);
      });
    });
    rows.sort((a, b) => a.appName.localeCompare(b.appName) || a.fieldLabel.localeCompare(b.fieldLabel));
    setProposals(rows);
    setAccepted(preChecked);
    setSummary(null);
  }, [fieldByKey, labelFor]);

  // ---- the run ------------------------------------------------------------
  const runEnrichment = async () => {
    if (!selectedApps.length || !askedFields.length) return;
    setRunning(true);
    setRunErr('');
    setUnavailable(false);
    setDemoMode(false);
    setRunProgress(0);
    setProposals([]);
    setAccepted(new Set());
    setSummary(null);

    const results = [];
    try {
      for (let i = 0; i < selectedApps.length; i += REQUEST_CHUNK) {
        const chunk = selectedApps.slice(i, i + REQUEST_CHUNK);
        const payload = {
          applications: chunk.map((fs) => ({
            id: fs.id,
            name: fs.name,
            vendorName: (fs.fields || {}).vendorName || '',
            description: (fs.fields || {}).description || '',
          })),
          fields: askedFields,
        };
        /* eslint-disable-next-line no-await-in-loop */
        const data = await api('/ai/enrich', { method: 'POST', body: JSON.stringify(payload) });
        (data.results || []).forEach((r) => results.push(r));
        setRunProgress(Math.round(Math.min(100, ((i + chunk.length) / selectedApps.length) * 100)));
        /* eslint-disable-next-line no-await-in-loop */
        await new Promise((r) => setTimeout(r, 0)); // let the progress bar paint
      }
      setRunProgress(100);
      buildProposals(results, selectedApps, false);
      // Name the fields that came back with nothing. A generic "no suggestions"
      // is the same message whether the model did not recognise the products or
      // the service silently discarded every field asked for — and that second
      // case is exactly how nine dead checkboxes went unnoticed. If a requested
      // field returns nothing for EVERY application, say which.
      const answered = new Set();
      results.forEach((r) => Object.keys(r.suggestions || {}).forEach((k) => answered.add(k)));
      const ignored = askedFields.filter((k) => !answered.has(k));
      if (!results.length) {
        setRunErr('The service returned no suggestions for this selection.');
      } else if (ignored.length) {
        setRunErr(`No value came back for ${ignored.map(labelFor).join(', ')} on any of the ${selectedApps.length} `
          + `application${selectedApps.length === 1 ? '' : 's'}. Either the model did not recognise the products, or the `
          + 'service is not configured to draft these fields.');
      }
    } catch (e) {
      setUnavailable(true);
      setRunErr((e && e.message) || 'AI enrichment service could not be reached.');
    } finally {
      setRunning(false);
    }
  };

  const runDemo = () => {
    if (!selectedApps.length || !askedFields.length) return;
    setRunErr('');
    setDemoMode(true);
    setRunProgress(100);
    const results = selectedApps.map((fs) => ({
      id: fs.id,
      suggestions: demoSuggestionsFor(fs, askedFields),
    }));
    buildProposals(results, selectedApps, true);
  };

  // ---- review state --------------------------------------------------------
  const usableRows = useMemo(() => proposals.filter((p) => p.usable), [proposals]);
  const acceptedRows = useMemo(
    () => usableRows.filter((p) => accepted.has(p.rowKey)),
    [usableRows, accepted]
  );
  const overwriteCount = acceptedRows.filter((p) => !p.currentEmpty).length;
  const acceptedAppCount = new Set(acceptedRows.map((p) => p.appId)).size;

  const groups = useMemo(() => {
    const byApp = new Map();
    proposals.forEach((p) => {
      const g = byApp.get(p.appId);
      if (g) g.rows.push(p);
      else byApp.set(p.appId, { appId: p.appId, appName: p.appName, rows: [p] });
    });
    return [...byApp.values()];
  }, [proposals]);

  const toggleRowAccept = (rowKey) => {
    const next = new Set(accepted);
    if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
    setAccepted(next);
    setSummary(null);
  };
  const toggleGroupAccept = (group) => {
    const usable = group.rows.filter((r) => r.usable);
    const all = usable.length > 0 && usable.every((r) => accepted.has(r.rowKey));
    const next = new Set(accepted);
    usable.forEach((r) => { if (all) next.delete(r.rowKey); else next.add(r.rowKey); });
    setAccepted(next);
    setSummary(null);
  };
  const selectAllEmpty = () => {
    const next = new Set(accepted);
    usableRows.forEach((r) => { if (r.currentEmpty) next.add(r.rowKey); });
    setAccepted(next);
    setSummary(null);
  };
  const clearAllAccepted = () => { setAccepted(new Set()); setSummary(null); };

  // ---- accept --------------------------------------------------------------
  const applyAccepted = async () => {
    const rows = acceptedRows;
    if (!rows.length) return;
    setConfirm(null);
    setApplying(true);
    setApplyProgress(0);
    setSummary(null);

    const byApp = new Map();
    rows.forEach((r) => {
      const g = byApp.get(r.appId) || { appId: r.appId, appName: r.appName, rows: [] };
      g.rows.push(r);
      byApp.set(r.appId, g);
    });
    const jobs = [...byApp.values()];
    const appsById = new Map(apps.map((a) => [a.id, a]));

    let okApps = 0;
    let okValues = 0;
    let failed = 0;
    const errors = [];

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      try {
        // Re-read before writing. `apps` was loaded when the screen mounted and
        // a run here is designed to take a while over hundreds of applications;
        // spreading that snapshot back into saveFactSheet replaced the whole
        // `fields` bag, so anything edited elsewhere in the session since the
        // load — on the fact sheet itself, by an import, by Forms Roundtrip —
        // was silently reverted by an accept. Both siblings already do this
        // (`FormsRoundtrip`, `SurveyWorkflow`); the in-memory list is only a
        // fallback for a sheet the store can no longer resolve.
        /* eslint-disable-next-line no-await-in-loop */
        const fs = (await store.getFactSheet(job.appId)) || appsById.get(job.appId);
        if (!fs) { failed += 1; continue; }
        const existing = fs.fields || {};
        const patch = {};
        job.rows.forEach((r) => { patch[r.fieldKey] = r.value; });
        const priorKeys = Array.isArray(existing.aiAssistedFields) ? existing.aiAssistedFields : [];
        const auditKeys = [...new Set([...priorKeys, ...job.rows.map((r) => r.fieldKey)])];
        /* eslint-disable-next-line no-await-in-loop */
        await store.saveFactSheet({
          ...fs,
          fields: {
            ...existing,        // merge — everything not proposed is preserved
            ...patch,
            aiAssisted: true,   // auditable marker: these values began as AI drafts
            aiAssistedAt: new Date().toISOString(),
            aiAssistedFields: auditKeys,
            aiAssistedSource: demoMode ? 'demo-placeholder' : 'ai-enrich',
          },
        });
        okApps += 1;
        okValues += job.rows.length;
      } catch (err) {
        failed += 1;
        if (errors.length < 5) errors.push(`${job.appName}: ${(err && err.message) || 'save failed'}`);
      }
      setApplyProgress(Math.round(((i + 1) / jobs.length) * 100));
      /* eslint-disable-next-line no-await-in-loop */
      await new Promise((r) => setTimeout(r, 0));
    }

    setApplying(false);
    setSummary({ okApps, okValues, failed, errors, demo: demoMode });
    setProposals([]);
    setAccepted(new Set());
    reload();
  };

  const busy = running || applying;

  // ---- render helpers ------------------------------------------------------
  const ConfidenceChip = ({ level }) => {
    const s = CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.low;
    return (
      <Chip size="small" label={s.label}
        sx={{ height: 20, fontWeight: 700, color: s.color, bgcolor: s.bg, border: `1px solid ${s.color}33` }} />
    );
  };

  const FieldPick = ({ k }) => (
    <FormControlLabel
      sx={{ m: 0, mr: 1.5 }}
      control={<Checkbox size="small" checked={fieldKeys.has(k)} onChange={() => toggleField(k)} disabled={busy} />}
      label={<span style={{ fontSize: 13 }}>{labelFor(k)}</span>}
    />
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <AutoAwesomeIcon sx={{ color: BLUE }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>AI enrichment</Typography>
        <Chip size="small" label="AI-DRAFTED — REVIEW REQUIRED"
          sx={{ height: 20, fontWeight: 700, bgcolor: '#EEF4FB', color: BLUE, border: `1px solid ${BLUE}44` }} />
        {demoMode && (
          <Chip size="small" icon={<ScienceIcon sx={{ fontSize: 15 }} />} label="DEMO MODE — PLACEHOLDER DATA"
            sx={{ height: 22, fontWeight: 700, bgcolor: '#FFF4E0', color: AMBER, border: `1px solid ${AMBER}` }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload applications">
          <span><Button size="small" startIcon={<RefreshIcon />} onClick={reload} disabled={busy}>Reload</Button></span>
        </Tooltip>
      </Stack>

      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
        Suggestions are <b>drafts based on general knowledge of the product</b> — they are not a
        statement about this client's environment, and they are frequently wrong about it. Every
        value must be verified with the application owner before it is treated as fact. Nothing on
        this screen is saved until you press <b>Accept</b>, and anything that would overwrite an
        existing value starts unchecked.
      </Alert>

      {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}

      {/* ------------------------------ 1. pick apps ------------------------ */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
          1 · Choose applications
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1.5 }}>
          <TextField
            size="small" placeholder="Search name or vendor…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <ToggleButtonGroup
            size="small" exclusive value={quickFilter}
            onChange={(e, v) => setQuickFilter(v || '')}
          >
            <ToggleButton value="description">Missing description</ToggleButton>
            <ToggleButton value="sixRClassification">Missing disposition</ToggleButton>
            <ToggleButton value="hostingType">Missing hosting</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={(
              <Checkbox
                size="small" checked={allMatchingSelected}
                indeterminate={!allMatchingSelected && someMatchingSelected}
                onChange={toggleAllMatching} disabled={!filtered.length || busy}
              />
            )}
            label={<span style={{ fontSize: 13, fontWeight: 600 }}>Select all {filtered.length} matching</span>}
          />
          <Chip size="small" label={`${selected.size} selected`}
            sx={{ fontWeight: 600, bgcolor: selected.size ? '#e3f0fa' : undefined }} />
          {filtered.length > ROW_CAP && (
            <Typography variant="caption" sx={{ color: GREY }}>
              showing the first {ROW_CAP} rows — "select all matching" still covers all {filtered.length}
            </Typography>
          )}
        </Stack>

        <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={headSx}>
                <TableCell padding="checkbox" />
                <TableCell>Application</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Hosting</TableCell>
                <TableCell>6R</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={18} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleRows.map((fs) => {
                const f = fs.fields || {};
                return (
                  <TableRow key={fs.id} hover selected={selected.has(fs.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selected.has(fs.id)} onChange={() => toggleApp(fs.id)} disabled={busy} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{fs.name}</TableCell>
                    <TableCell sx={{ color: GREY }}>{f.vendorName || '—'}</TableCell>
                    <TableCell sx={{ color: GREY, maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {f.description || <span style={{ color: '#C77700', fontWeight: 600 }}>missing</span>}
                    </TableCell>
                    <TableCell sx={{ color: GREY }}>{f.hostingType || '—'}</TableCell>
                    <TableCell sx={{ color: GREY }}>{f.sixRClassification || f.cmDisposition || '—'}</TableCell>
                  </TableRow>
                );
              })}
              {!loading && !visibleRows.length && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: GREY, textAlign: 'center', py: 3 }}>
                    No applications match these filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Paper>

      {/* ------------------------------ 2. pick fields ---------------------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>
          2 · Choose what to ask for
        </Typography>
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap' }}>
          {coreFields.map((k) => <FieldPick key={k} k={k} />)}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 0.5 }}>
          Standard discovery questions — a draft answer to open the interview with, never the answer itself.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {discoveryFields.map((k) => <FieldPick key={k} k={k} />)}
        </Box>
        {notDraftable.length > 0 && (
          <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
            Not offered here, deliberately:{' '}
            {notDraftable.map((g, i) => (
              <span key={g.why}>
                {i > 0 ? '; ' : ''}
                <b>{g.labels.join(', ')}</b> — {g.why}
              </span>
            ))}
            . Capture these in the interview or on the fact sheet.
          </Typography>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant="contained" startIcon={<AutoAwesomeIcon />}
            disabled={busy || !selected.size || !askedFields.length}
            onClick={runEnrichment}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {running ? `Drafting ${runProgress}%` : `Draft suggestions for ${selected.size} application${selected.size === 1 ? '' : 's'}`}
          </Button>
          {unavailable && (
            <Button variant="outlined" startIcon={<ScienceIcon />} disabled={busy || !selected.size || !askedFields.length}
              onClick={runDemo} sx={{ color: AMBER, borderColor: AMBER }}>
              Run DEMO MODE instead
            </Button>
          )}
          <Typography variant="caption" sx={{ color: GREY }}>
            {askedFields.length} field{askedFields.length === 1 ? '' : 's'} requested · nothing is written by this step
          </Typography>
        </Stack>
        {running && <LinearProgress variant="determinate" value={runProgress} sx={{ mt: 1.5 }} />}
      </Paper>

      {unavailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>AI enrichment is not enabled for this environment</AlertTitle>
          The <code>/ai/enrich</code> service did not respond{runErr ? ` (${runErr})` : ''}. Nothing was
          changed. You can still walk the full review-and-accept workflow with <b>DEMO MODE</b>, which
          generates clearly-labelled placeholder text locally from the application name and vendor —
          it calls no AI service and contains no real knowledge about these products.
        </Alert>
      )}
      {!unavailable && runErr && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRunErr('')}>{runErr}</Alert>}

      {demoMode && !!proposals.length && (
        <Alert severity="warning" icon={<ScienceIcon />} sx={{ mb: 2, border: `2px solid ${AMBER}` }}>
          <AlertTitle sx={{ fontWeight: 800 }}>DEMO MODE — these are placeholders, not suggestions</AlertTitle>
          Every value below was fabricated locally to demonstrate the workflow. Do not accept any of
          it into a real client inventory.
        </Alert>
      )}

      {/* ------------------------------ 3. review --------------------------- */}
      {!!proposals.length && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
              3 · Review proposals
            </Typography>
            <Chip size="small" label={`${proposals.length} proposed value${proposals.length === 1 ? '' : 's'}`} sx={{ fontWeight: 600 }} />
            <Chip size="small" label={`${acceptedRows.length} checked`}
              sx={{ fontWeight: 600, bgcolor: acceptedRows.length ? '#e3f0fa' : undefined }} />
            {overwriteCount > 0 && (
              <Chip size="small" label={`${overwriteCount} would overwrite existing data`}
                sx={{ fontWeight: 700, bgcolor: '#FDECEA', color: '#B0342C' }} />
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={selectAllEmpty} disabled={applying}>Check all empty fields</Button>
            <Button size="small" onClick={clearAllAccepted} disabled={applying}>Clear all</Button>
          </Stack>

          <Paper variant="outlined" sx={{ maxHeight: 460, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={headSx}>
                  <TableCell padding="checkbox" />
                  <TableCell>Field</TableCell>
                  <TableCell>Current value</TableCell>
                  <TableCell>AI-drafted proposal</TableCell>
                  <TableCell>Confidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => {
                  const usable = g.rows.filter((r) => r.usable);
                  const allOn = usable.length > 0 && usable.every((r) => accepted.has(r.rowKey));
                  const someOn = usable.some((r) => accepted.has(r.rowKey));
                  return (
                    <React.Fragment key={g.appId}>
                      <TableRow sx={{ bgcolor: '#F4F7FA' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small" checked={allOn} indeterminate={!allOn && someOn}
                            onChange={() => toggleGroupAccept(g)} disabled={applying || !usable.length}
                          />
                        </TableCell>
                        <TableCell colSpan={4} sx={{ fontWeight: 700, color: NAVY }}>
                          {g.appName}
                          <Typography component="span" variant="caption" sx={{ color: GREY, ml: 1 }}>
                            {g.rows.length} proposal{g.rows.length === 1 ? '' : 's'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {g.rows.map((r) => (
                        <TableRow key={r.rowKey} hover selected={accepted.has(r.rowKey)}>
                          <TableCell padding="checkbox">
                            <Tooltip title={r.usable
                              ? (r.currentEmpty
                                ? 'Field is empty — pre-checked, still your call'
                                : 'This would OVERWRITE an existing value — check it deliberately')
                              : `Cannot be accepted: proposed value is ${r.reason}`}>
                              <span>
                                <Checkbox
                                  size="small" checked={accepted.has(r.rowKey)}
                                  disabled={applying || !r.usable}
                                  onChange={() => toggleRowAccept(r.rowKey)}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ color: GREY, whiteSpace: 'nowrap' }}>{r.fieldLabel}</TableCell>
                          <TableCell sx={{ maxWidth: 260, color: r.currentEmpty ? '#C77700' : NAVY }}>
                            {r.currentEmpty
                              ? <em>empty</em>
                              : <Tooltip title={displayValue(r.current)}><span>{displayValue(r.current)}</span></Tooltip>}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 420 }}>
                            <Tooltip title={`Why the model proposed this: ${r.rationale}`}>
                              <Box component="span" sx={{ borderBottom: '1px dotted #9AA6B2', cursor: 'help' }}>
                                {displayValue(r.value)}
                              </Box>
                            </Tooltip>
                            {!r.usable && (
                              <Chip size="small" label={`rejected — ${r.reason}`}
                                sx={{ ml: 1, height: 18, fontWeight: 700, bgcolor: '#FDECEA', color: '#B0342C' }} />
                            )}
                            {!r.currentEmpty && r.usable && (
                              <Chip size="small" label="overwrites"
                                sx={{ ml: 1, height: 18, fontWeight: 700, bgcolor: '#FFF4E0', color: AMBER }} />
                            )}
                            {r.demo && (
                              <Chip size="small" label="DEMO"
                                sx={{ ml: 1, height: 18, fontWeight: 800, bgcolor: '#FFF4E0', color: AMBER, border: `1px solid ${AMBER}` }} />
                            )}
                          </TableCell>
                          <TableCell><ConfidenceChip level={r.confidence} /></TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1.5 }}>
            Hover a proposed value to read the rationale. Values that are not a legal option for the
            field cannot be accepted. Accepted values are written with an <code>aiAssisted</code>
            {' '}marker on the fact sheet so it stays clear which data began as an AI draft.
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
            <Button
              variant="contained" disabled={applying || !acceptedRows.length}
              onClick={() => setConfirm(true)}
              sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
            >
              {applying ? `Saving ${applyProgress}%` : `Accept ${acceptedRows.length} value${acceptedRows.length === 1 ? '' : 's'}`}
            </Button>
            <Typography variant="caption" sx={{ color: GREY }}>
              across {acceptedAppCount} application{acceptedAppCount === 1 ? '' : 's'}
              {overwriteCount > 0 ? ` · ${overwriteCount} overwrite existing values` : ''}
            </Typography>
          </Stack>
          {applying && <LinearProgress variant="determinate" value={applyProgress} sx={{ mt: 1.5 }} />}
        </Paper>
      )}

      {summary && (
        <Alert severity={summary.failed ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setSummary(null)}>
          <AlertTitle>Accepted</AlertTitle>
          Wrote {summary.okValues} value{summary.okValues === 1 ? '' : 's'} across {summary.okApps}{' '}
          application{summary.okApps === 1 ? '' : 's'}
          {summary.failed ? ` · ${summary.failed} could not be saved` : ''}.
          {summary.demo && ' These were DEMO placeholders — clear them before this workspace is used for real.'}
          {!!summary.errors.length && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {summary.errors.map((e, i) => <li key={i}>{e}</li>)}
            </Box>
          )}
        </Alert>
      )}

      <Dialog open={!!confirm} onClose={() => !applying && setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Accept AI-drafted values?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will write <b>{acceptedRows.length}</b> value{acceptedRows.length === 1 ? '' : 's'} across{' '}
            <b>{acceptedAppCount}</b> application{acceptedAppCount === 1 ? '' : 's'}. Every other field on
            those fact sheets is left exactly as it is.
          </Typography>
          {overwriteCount > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <b>{overwriteCount}</b> of these replace a value that is already recorded.
            </Alert>
          )}
          {demoMode && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              These are <b>DEMO placeholders</b>, not real suggestions. Do not accept them into a
              client inventory.
            </Alert>
          )}
          <Typography variant="caption" sx={{ color: GREY }}>
            Each fact sheet touched is stamped <code>aiAssisted: true</code> with the list of affected
            fields, so these values remain identifiable as AI drafts until an owner confirms them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={applying}>Cancel</Button>
          <Button variant="contained" onClick={applyAccepted} disabled={applying}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
            Accept and save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
